import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Phone } from "lucide-react";
import useSEO from "../hooks/useSEO";
import Reveal from "../components/Reveal";
import JourneyPath from "../components/JourneyPath";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { IMAGES, CONTACTS } from "../data/content";

const VIGNETTES = [
    {
        kicker: "He steps in",
        title: "The untranslated menu",
        body: "A menu arrives in a language you don't read. Your guide reads it aloud, marks the three dishes worth crossing the street for — then melts away while you decide.",
    },
    {
        kicker: "He steps in",
        title: "The full table",
        body: "The restaurant says it's booked out. Minutes later you're seated at the best table in the room. You never made the call; you never needed to.",
    },
    {
        kicker: "He steps in",
        title: "The crowded gate",
        body: "A festival has swallowed the morning's plan. He quietly reroutes the day around the crowds — you notice only that it stayed beautiful.",
    },
    {
        kicker: "He steps in",
        title: "The sudden change",
        body: "A cancelled train, a closed road, a storm. It's all rearranged before you finish your coffee. You hear the new plan, never the problem.",
    },
    {
        kicker: "He steps back",
        title: "The quiet hour",
        body: "A family dinner, a slow sunset, a moment that belongs only to you. No one hovers, no one narrates. He is there — but he is not there.",
    },
];

const PRINCIPLES = [
    { title: "Privacy first", body: "Your journey is yours alone; we protect that at every step." },
    { title: "Always within reach", body: "Help is a quiet word away, at any hour." },
    { title: "Never in the way", body: "Guidance without intrusion is the whole art." },
    { title: "You set the pace", body: "Fast mornings or slow ones — the day follows you." },
];

const INCLUSIONS = [
    "Consultation sessions with senior travel architects",
    "Full itinerary ownership — you approve every element before booking",
    "Private guide following the HIT but HINT philosophy",
    "Priority partnerships and preferred rates",
    "A dedicated consultant from first call to final day",
    "Post-trip follow-up — feedback gathered, preferences remembered",
];

const OCCASIONS = ["Families up to ten", "Special occasions", "Corporate retreats", "Luxury holidays"];

const MaskedLine = ({ children, delay }) => (
    <span className="block overflow-hidden pb-1">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const Fade = ({ children, delay, className = "" }) => (
    <motion.div
        className={className}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
        {children}
    </motion.div>
);

export default function Bespoke() {
    const navigate = useNavigate();
    useSEO({
        title: "Bespoke Travel from India | HIT but HINT Private Guiding — Journeys Unscripted",
        description:
            "Bespoke travel, guided the HIT but HINT way: fully private journeys from India where your private guide is present when you need him and invisible when you don't. Nothing templated, nothing confirmed without your sign-off.",
        ogImage: IMAGES.bespokeHero.src,
    });

    const goContact = () => navigate("/", { state: { scrollTo: "contact" } });

    return (
        <main data-testid="bespoke-page">
            {/* Hero */}
            <section className="relative min-h-[88svh] overflow-hidden bg-navy flex items-center" data-testid="bespoke-hero">
                <div className="absolute inset-0">
                    <motion.img
                        src={IMAGES.bespokeHero.src}
                        alt={IMAGES.bespokeHero.alt}
                        initial={{ scale: 1.14, opacity: 0.5 }}
                        animate={{ scale: 1.05, opacity: 1 }}
                        transition={{ duration: 2.4, ease: [0.22, 1, 0.36, 1] }}
                        className="h-full w-full object-cover"
                    />
                    <div className="absolute inset-0 bg-navy/65" />
                    <div className="absolute inset-0 bg-gradient-to-b from-navy/60 via-transparent to-navy/75" />
                </div>
                <div className="relative z-10 container-luxe pt-28 pb-20">
                    <Fade delay={0.2}>
                        <p className="eyebrow mb-6">Bespoke Private Tour · The HIT but HINT Philosophy</p>
                    </Fade>
                    <h1 className="font-serif text-cream text-4xl sm:text-5xl lg:text-6xl leading-[1.12] tracking-tight max-w-3xl">
                        <MaskedLine delay={0.35}>He Is There,</MaskedLine>
                        <MaskedLine delay={0.5}>
                            <span className="italic text-gold">but He Is Not There.</span>
                        </MaskedLine>
                    </h1>
                    <motion.div
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 1, delay: 0.9, ease: [0.22, 1, 0.36, 1] }}
                        className="origin-left h-px w-24 bg-gold mt-8"
                    />
                    <Fade delay={1.05} className="mt-6">
                        <p className="text-cream/85 font-light max-w-xl text-base sm:text-lg leading-relaxed">
                            Our HIT but HINT philosophy — the gold standard of private guiding.
                        </p>
                    </Fade>
                </div>
            </section>

            {/* Intro */}
            <section className="section-pad" data-testid="bespoke-intro">
                <div className="container-luxe grid gap-12 lg:grid-cols-12">
                    <div className="lg:col-span-4">
                        <Reveal>
                            <p className="eyebrow mb-5">The Philosophy</p>
                            <h2 className="font-serif text-navy text-3xl sm:text-4xl leading-snug">
                                Guided, <span className="italic text-gold">unseen</span>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="lg:col-span-8">
                        <Reveal delay={0.1}>
                            <p className="font-serif text-navy text-xl sm:text-2xl leading-relaxed">
                                To respect the guest&rsquo;s privacy, every Bespoke journey is guided the HIT but HINT
                                way. Your private guide is always present, alert and ready — yet invisible the moment
                                you want your own space.
                            </p>
                            <p className="mt-6 font-light leading-relaxed max-w-2xl">
                                You travel freely; we stay quietly alongside. It is the difference between being shown
                                around and being looked after — the highest form of hospitality, and the hardest to
                                notice, which is exactly the point.
                            </p>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Split panel */}
            <section className="pb-4" data-testid="bespoke-split-panel">
                <div className="container-luxe grid gap-8 md:grid-cols-2">
                    <Reveal>
                        <div className="h-full bg-white border border-line border-t-2 border-t-gold p-8 sm:p-10" data-testid="panel-present">
                            <p className="eyebrow mb-4">Solid line</p>
                            <h3 className="font-serif text-navy text-2xl mb-6">Present when you need us</h3>
                            <ul className="space-y-3.5 text-sm font-light leading-relaxed">
                                <li>Translating, negotiating, arranging the best table in the room.</li>
                                <li>Tickets, timings and the unexpected — handled before you feel them.</li>
                                <li>Always alert, always a quiet word away.</li>
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="h-full bg-white border border-line border-t-2 border-t-dashed border-t-gold p-8 sm:p-10" data-testid="panel-invisible">
                            <p className="eyebrow mb-4">Dashed line</p>
                            <h3 className="font-serif text-navy text-2xl mb-6">Invisible when you don&rsquo;t</h3>
                            <ul className="space-y-3.5 text-sm font-light leading-relaxed">
                                <li>He steps back the moment you want your own space.</li>
                                <li>No hovering, no narrating, no audience.</li>
                                <li>You travel freely; we stay quietly alongside.</li>
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* How it feels */}
            <section className="section-pad" data-testid="bespoke-vignettes">
                <div className="container-luxe">
                    <Reveal className="mb-14 max-w-2xl">
                        <p className="eyebrow mb-5">How It Feels</p>
                        <h2 className="font-serif text-navy text-3xl sm:text-4xl leading-snug">
                            Five moments you&rsquo;ll <span className="italic text-gold">remember</span>
                        </h2>
                    </Reveal>
                    <div className="space-y-0">
                        {VIGNETTES.map((v, i) => (
                            <Reveal key={v.title} delay={0.05 * i}>
                                <div
                                    className={`grid md:grid-cols-12 gap-4 md:gap-8 py-8 border-t border-line ${i === VIGNETTES.length - 1 ? "border-b" : ""}`}
                                    data-testid={`vignette-${i}`}
                                >
                                    <div className="md:col-span-3 flex md:block items-center">
                                        <span
                                            className={`text-[11px] tracking-[0.2em] uppercase ${v.kicker === "He steps in" ? "text-gold" : "text-forest"}`}
                                        >
                                            {v.kicker}
                                        </span>
                                    </div>
                                    <div className="md:col-span-9">
                                        <h3 className="font-serif text-navy text-xl sm:text-2xl">{v.title}</h3>
                                        <p className="mt-2.5 font-light leading-relaxed max-w-2xl">{v.body}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Principles */}
            <section className="section-pad pt-0" data-testid="bespoke-principles">
                <div className="container-luxe">
                    <Reveal className="mb-12">
                        <p className="eyebrow mb-5">Principles</p>
                    </Reveal>
                    <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {PRINCIPLES.map((p, i) => (
                            <Reveal key={p.title} delay={0.08 * i}>
                                <div className="border-t border-gold/50 pt-6" data-testid={`principle-${i}`}>
                                    <p className="font-serif italic text-gold text-lg">{String(i + 1).padStart(2, "0")}</p>
                                    <h3 className="mt-3 font-serif text-navy text-xl">{p.title}</h3>
                                    <p className="mt-2 text-sm font-light leading-relaxed">{p.body}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Inclusions + process */}
            <section className="section-pad bg-navy text-cream" data-testid="bespoke-inclusions">
                <div className="container-luxe grid gap-14 lg:grid-cols-2">
                    <div>
                        <Reveal>
                            <p className="eyebrow mb-5">The Bespoke Private Tour</p>
                            <h2 className="font-serif text-3xl sm:text-4xl leading-snug">
                                Everything included, <span className="italic text-gold">nothing imposed</span>
                            </h2>
                            <ul className="mt-8 space-y-4">
                                {INCLUSIONS.map((inc) => (
                                    <li key={inc} className="flex items-start gap-3 text-sm font-light text-cream/85">
                                        <Check size={16} strokeWidth={2.5} className="text-gold mt-1 shrink-0" />
                                        {inc}
                                    </li>
                                ))}
                            </ul>
                        </Reveal>
                    </div>
                    <div>
                        <Reveal>
                            <p className="eyebrow mb-5">The Consultation Process</p>
                            <h2 className="font-serif text-3xl sm:text-4xl leading-snug">
                                You approve <span className="italic text-gold">every element</span>
                            </h2>
                            <div className="mt-8 space-y-8">
                                {[
                                    { n: "01", t: "Deep-dive conversations", b: "Multiple unhurried calls with our senior travel architects — how you sleep, eat, wander and wind down." },
                                    { n: "02", t: "The draft journey", b: "A day-by-day journey drawn only from your brief, revised until every stay, guide and activity is exactly yours." },
                                    { n: "03", t: "Sign-off, then it's real", b: "Nothing is confirmed without your approval. Once you sign off, we execute flawlessly behind the scenes." },
                                ].map((s) => (
                                    <div key={s.n} className="flex gap-5">
                                        <span className="font-serif italic text-gold text-2xl leading-none pt-1">{s.n}</span>
                                        <div>
                                            <h3 className="font-serif text-xl">{s.t}</h3>
                                            <p className="mt-1.5 text-sm font-light text-cream/70 leading-relaxed">{s.b}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Occasions */}
            <section className="section-pad" data-testid="bespoke-occasions">
                <div className="container-luxe">
                    <Reveal className="mb-10">
                        <p className="eyebrow mb-5">Ideal Occasions</p>
                        <h2 className="font-serif text-navy text-3xl sm:text-4xl leading-snug">
                            Made for moments that <span className="italic text-gold">matter</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <ul className="flex flex-wrap gap-3 sm:gap-4">
                            {OCCASIONS.map((o) => (
                                <li
                                    key={o}
                                    className="inline-flex items-center gap-2.5 rounded-full border border-gold/50 px-5 py-2.5 text-sm font-light text-navy"
                                    data-testid="occasion-pill"
                                >
                                    <span className="h-1 w-1 rounded-full bg-gold" aria-hidden="true" />
                                    {o}
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>
            </section>

            {/* Pull quote */}
            <section className="pb-8" data-testid="bespoke-pullquote">
                <div className="container-luxe">
                    <JourneyPath />
                    <Reveal delay={0.1}>
                        <blockquote className="text-center max-w-3xl mx-auto py-14">
                            <p className="font-serif italic text-navy text-2xl sm:text-3xl lg:text-[2.4rem] leading-snug">
                                &ldquo;Nothing borrowed, nothing templated: the journey is written{" "}
                                <span className="text-gold">entirely by you.</span>&rdquo;
                            </p>
                        </blockquote>
                    </Reveal>
                    <JourneyPath />
                </div>
            </section>

            {/* CTA */}
            <section className="section-pad pt-10" data-testid="bespoke-cta">
                <div className="container-luxe">
                    <Reveal>
                        <div className="bg-navy text-cream px-8 py-14 sm:px-14 text-center relative overflow-hidden">
                            <span className="absolute top-0 left-8 right-8 h-px bg-gold/40" aria-hidden="true" />
                            <span className="absolute bottom-0 left-8 right-8 h-px bg-gold/40" aria-hidden="true" />
                            <p className="eyebrow mb-4">Bespoke Private Tour</p>
                            <h2 className="font-serif text-3xl sm:text-4xl leading-snug max-w-2xl mx-auto">
                                Travel entirely <span className="italic text-gold">your way</span>
                            </h2>
                            <p className="mt-4 text-cream/70 font-light max-w-xl mx-auto">
                                A free, unhurried consultation is where every Bespoke journey begins.
                            </p>
                            <div className="mt-9 flex flex-col sm:flex-row justify-center gap-4">
                                <button data-testid="bespoke-enquire-cta" onClick={goContact} className="btn-gold">
                                    Enquire about a Bespoke Journey
                                </button>
                                <a
                                    data-testid="bespoke-whatsapp-cta"
                                    href={CONTACTS[0].whatsapp}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-ghost-light"
                                >
                                    <WhatsAppIcon size={15} /> WhatsApp Us
                                </a>
                                <a data-testid="bespoke-call-cta" href={`tel:${CONTACTS[0].tel}`} className="btn-ghost-light">
                                    <Phone size={15} /> {CONTACTS[0].phoneDisplay}
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>
        </main>
    );
}
